import {logger} from 'common'
import {usePathname} from 'next/navigation'
import Script from 'next/script'
import {User} from 'next-auth'
import {useSession} from 'next-auth/react'
import {FC, useCallback, useEffect, useRef, useState} from 'react'

import {MISSIVE_CHAT_ID, MISSIVE_SCRIPT_URL} from '@/utils/missive/constants'
import {
  hasActiveMissiveChat,
  identifyMissiveUser,
  isMissiveChatLeftOpen,
  isMissiveChatShown,
  markMissiveChatActivity,
  setMissiveChatLeftOpen,
} from '@/utils/missive/helpers'
import {analytics} from '@/utils/tracking/analytics'

import {MissiveChatLauncher} from './MissiveChatLauncher'

// How far the visitor has got: loading it in the background, showing the widget's own button, or
// opening the panel. A ref, not state - the widget callbacks are registered once and would
// otherwise read whatever this was when the script was requested.
type ChatIntent = 'preload' | 'reveal' | 'open'

/**
 * Loaded on demand: the dummy `MissiveChatLauncher` holds the corner and the widget's
 * ~133 kB only arrive once a visitor reaches for it. The download starts on hover/touch/focus, so
 * the click usually finds it already loaded, if not, loading will be shown.
 *
 * `chat.hidden` keeps the widget's own button down until we call `showButton()`, so a hover that
 * never becomes a click changes nothing on screen, and a load landing after the visitor navigated
 * cannot put a button on a path that must not have one.
 *
 * Exception: a conversation still running is loaded and shown up front, to surface an agent's
 * reply. See `hasActiveMissiveChat`.
 */
export const Missive: FC = () => {
  const [scriptRequested, setScriptRequested] = useState(false)
  const [chatReady, setChatReady] = useState(false)

  // The widget's own button has taken over from the launcher.
  const [revealed, setRevealed] = useState(false)

  // Spinner: a click that arrived before the preload finished.
  const [opening, setOpening] = useState(false)

  // next/script caches by `src`, and caches a failed load as readily as a good one, so a retry has
  // to ask for a URL it has not seen. The counter is what makes the next one different.
  const [loadAttempt, setLoadAttempt] = useState(0)

  const {data: session} = useSession()
  const pathname = usePathname()
  const lastSessionUser = useRef<User | undefined>(undefined)
  const intent = useRef<ChatIntent>('preload')
  const loadFailed = useRef(false)

  const showChat = isMissiveChatShown(pathname ?? '/')

  const user = session?.user

  const reveal = useCallback(() => {
    window.MissiveChat?.showButton()
    if (intent.current === 'open') {
      window.MissiveChat?.open()
    }
    setRevealed(true)
    setOpening(false)
  }, [])

  // Starts the download without touching anything on screen. The config is written here rather than
  // from an effect: the widget reads it as it initialises, and setting it before the state update
  // that mounts <Script> is what puts it in place in time.
  const requestScript = useCallback(() => {
    if (!window.MissiveChatConfig) {
      window.MissiveChatConfig = {
        id: MISSIVE_CHAT_ID,
        // `rememberOpenState` off: reopening a panel left open is ours to do (see the
        // returning-visitor effect), or the widget opens it on a mere hover, behind our back.
        chat: {hidden: true, rememberOpenState: false},
        // The widget's own readiness signal - guarantees `window.MissiveChat` exists.
        onReady: () => {
          setChatReady(true)
          if (intent.current !== 'preload' && isMissiveChatShown(window.location.pathname)) {
            reveal()
          } else {
            // Nothing to reveal - it was only a preload, or the visitor has moved on to a path
            // without chat. Drop the spinner either way, or the launcher is still mid-spin when
            // they come back.
            setOpening(false)
          }
        },
        onChatStarted: () => {
          window.MissiveChat?.setMeta({page: window.location.pathname})
          markMissiveChatActivity()
        },
        // Slides the active window on every message.
        onNewMessage: () => markMissiveChatActivity(),
        onOpen: () => setMissiveChatLeftOpen(true),
        onClose: () => setMissiveChatLeftOpen(false),
      }
    }

    // Any later sign of intent retries a failed load, not only a click - an active chat that
    // failed to auto-reveal comes back on the next hover.
    if (loadFailed.current) {
      loadFailed.current = false
      setLoadAttempt((attempt) => attempt + 1)
    }

    setScriptRequested(true)
  }, [reveal])

  const openChat = useCallback(() => {
    analytics.track('Chat - Opened')
    intent.current = 'open'
    requestScript()

    // Not `window.MissiveChat`: it appears as soon as the loader runs, well before the widget's
    // iframe can act on `showButton()`/`open()`. Until `onReady`, the spinner holds the corner.
    if (chatReady) {
      reveal()
      return
    }

    setOpening(true)
  }, [chatReady, requestScript, reveal])

  // Returning visitor: a panel left open is reopened, a running conversation is shown. Reading
  // storage from an effect keeps SSR and the first client render identical.
  useEffect(() => {
    if (!showChat || intent.current !== 'preload') {
      return
    }

    if (isMissiveChatLeftOpen()) {
      intent.current = 'open'
      requestScript()
    } else if (hasActiveMissiveChat()) {
      intent.current = 'reveal'
      requestScript()
    }
  }, [showChat, requestScript])

  // Hands the visitor's identity to the widget. Gated on `chatReady` because `window.MissiveChat`
  // calls only reach the widget once its `onReady` has fired, and re-run on it so a session that
  // was already in place when the chat loads is still identified.
  useEffect(() => {
    try {
      // user logged in (alternatively just first load of page)
      if (chatReady && !lastSessionUser?.current && user) {
        identifyMissiveUser(user.name, user.email, user.missiveDigest)
        lastSessionUser.current = user
      }
      // user logged out
      if (chatReady && lastSessionUser?.current && !user) {
        identifyMissiveUser(undefined, undefined, undefined)
        lastSessionUser.current = user
      }
    } catch (e) {
      logger.error(e)
    }
  }, [user, chatReady])

  // Only relevant once the widget owns the corner; before that the launcher comes and goes with
  // the path.
  useEffect(() => {
    if (!revealed) {
      return
    }

    if (showChat) {
      window.MissiveChat?.showButton()
    } else {
      window.MissiveChat?.close()
      window.MissiveChat?.hideButton()
    }
  }, [showChat, revealed])

  return (
    <>
      {/* Until the widget's own button is up, the launcher is the button. */}
      {showChat && !revealed && (
        <MissiveChatLauncher loading={opening} onOpen={openChat} onIntent={requestScript} />
      )}
      {/*
        Stays mounted once requested: unmounting cancels neither the download nor the widget's init,
        only the `onReady` everything here hangs off.
      */}
      {scriptRequested && (
        <Script
          key={loadAttempt}
          src={`${MISSIVE_SCRIPT_URL}${loadAttempt ? `?retry=${loadAttempt}` : ''}`}
          strategy="afterInteractive"
          onError={(e) => {
            // Drops the spinner and marks the attempt failed, so the next sign of intent asks for a
            // fresh URL rather than a cached failure. Only a click is forgotten - a retry landing
            // later must not pop the panel open, but a pending reveal still should happen.
            logger.error(e)
            if (intent.current === 'open') {
              intent.current = 'preload'
            }
            loadFailed.current = true
            setOpening(false)
          }}
        />
      )}
    </>
  )
}
